import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  VirtualizedCombobox,
  VirtualizedFormSelect,
} from '@roger/ui-components';
import { Check, Loader2 } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { getAgencies, updateAgencyTimezone } from '../../services/api';
import type { AgencyResponse } from '../../services/api/types.gen';
import { useStore } from '../../store';
import { getUserFacingApiMessage } from '../../utils/apiErrorDetail';
import {
  TIMEZONE_OPTIONS,
  formatTimezoneLabel,
} from '../../utils/timezoneOptions';

function formatAgencyLabel(agency: AgencyResponse): string {
  const systemName = agency.system_name.trim();
  const writtenName = (agency.written_name ?? '').trim();
  if (!writtenName || writtenName.toLowerCase() === systemName.toLowerCase()) {
    return systemName;
  }
  return `${writtenName} (${systemName})`;
}

/**
 * Sets any agency's timezone. It lives in Internal Tools, not on the agency's
 * own admin page, because the write is Strawhat-only: staff pick the target
 * agency here rather than switching into it.
 */
const AgencyTimezonePanel: React.FC = () => {
  const { user, showError, showSuccess, checkAuthStatus } = useStore(
    useShallow(state => ({
      user: state.user,
      showError: state.showError,
      showSuccess: state.showSuccess,
      checkAuthStatus: state.checkAuthStatus,
    }))
  );
  const [agencies, setAgencies] = useState<AgencyResponse[]>([]);
  const [loadingAgencies, setLoadingAgencies] = useState(true);
  const [agencyId, setAgencyId] = useState('');
  const [timezone, setTimezone] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    const loadAgencies = async () => {
      try {
        const response = await getAgencies({
          query: { limit: 1000 },
          throwOnError: true,
        });
        if (active) setAgencies(response.data ?? []);
      } catch (error) {
        console.error('Failed to load agencies for timezone', error);
        if (active) {
          showError(
            'Agencies unavailable',
            getUserFacingApiMessage(error, 'Unable to load agencies.')
          );
        }
      } finally {
        if (active) setLoadingAgencies(false);
      }
    };
    void loadAgencies();
    return () => {
      active = false;
    };
  }, [showError]);

  const agencyOptions = useMemo(
    () =>
      agencies.map(agency => ({
        value: agency.id,
        label: formatAgencyLabel(agency),
      })),
    [agencies]
  );
  const selectedAgency = agencies.find(agency => agency.id === agencyId);
  const canSave = Boolean(
    selectedAgency &&
    timezone !== '' &&
    timezone !== selectedAgency.timezone &&
    !saving
  );

  const selectAgency = (value: string) => {
    setAgencyId(value);
    setTimezone(agencies.find(agency => agency.id === value)?.timezone ?? '');
  };

  const handleSave = async () => {
    if (!selectedAgency) return;
    setSaving(true);
    try {
      // throwOnError so a refused write reaches the catch; the client's
      // default resolves with an error field and would report success.
      const response = await updateAgencyTimezone({
        path: { agency_id: selectedAgency.id },
        body: { timezone },
        throwOnError: true,
      });
      const saved = response.data;
      setAgencies(previous =>
        previous.map(agency => (agency.id === saved.id ? saved : agency))
      );
      setTimezone(saved.timezone);
      showSuccess(
        'Agency timezone updated',
        `${formatAgencyLabel(saved)} now uses ${formatTimezoneLabel(saved.timezone)}.`
      );
      // The signed-in agency's timezone also rides on /auth/me.
      if (saved.id === user?.agency?.id) {
        await checkAuthStatus();
      }
    } catch (error) {
      console.error('Failed to update agency timezone', error);
      showError(
        'Timezone not saved',
        getUserFacingApiMessage(error, 'Unable to update the agency timezone.')
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Agency Timezone</CardTitle>
        <CardDescription>
          Sets which calendar day it is for an agency&apos;s business dates. A
          clinic&apos;s own timezone overrides it.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className='grid gap-4 md:max-w-md'>
          <VirtualizedFormSelect
            key={`timezone-agency-${agencyId || 'none'}-${agencies.length}`}
            label='Agency'
            placeholder={
              loadingAgencies ? 'Loading agencies…' : 'Search agencies…'
            }
            options={agencyOptions}
            sortOptions
            selectSize='sm'
            isClearable={false}
            defaultValue={agencyId}
            onChange={event => selectAgency(event.target.value)}
            menuPortalTarget={null}
            menuPosition='absolute'
            disabled={loadingAgencies || saving}
          />
          {selectedAgency && (
            <div className='grid gap-2'>
              <label
                htmlFor='agency_timezone_target'
                className='text-sm font-medium'
              >
                Timezone
              </label>
              <VirtualizedCombobox
                id='agency_timezone_target'
                options={TIMEZONE_OPTIONS}
                value={timezone}
                onValueChange={value => setTimezone(value || '')}
                placeholder='Select a timezone'
                searchPlaceholder='Search timezones...'
                height='300px'
              />
              <div>
                <Button onClick={handleSave} disabled={!canSave}>
                  {saving ? (
                    <>
                      <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className='mr-2 h-4 w-4' />
                      Save timezone
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AgencyTimezonePanel;
